import { client } from '@repo/api-portal-sdk-generated/client'
import type { QueryClient } from '@tanstack/react-query'
import { isSessionDeathCode } from './auth-recovery-codes'

type GetCsrfToken = (queryClient: QueryClient) => string | null | undefined
type IsSessionAuthenticated = (queryClient: QueryClient) => boolean

interface BrowserSdkOptions {
	queryClient: QueryClient
	getCsrfToken: GetCsrfToken
	isSessionAuthenticated: IsSessionAuthenticated
	onUnauthorized: () => Promise<void> | void
}

interface BrowserSdkConfig extends Partial<BrowserSdkOptions> {
	requestInterceptorId?: number
	responseInterceptorId?: number
}

const BROWSER_SDK_CONFIG_KEY = Symbol.for('sealedger.api-portal-browser-sdk')

type ConfigurableClient = typeof client & {
	[BROWSER_SDK_CONFIG_KEY]?: BrowserSdkConfig
}

export function configureApiPortalBrowserSdk(options: BrowserSdkOptions) {
	if (typeof window === 'undefined') {
		return
	}

	const config = getBrowserSdkConfig()
	config.queryClient = options.queryClient
	config.getCsrfToken = options.getCsrfToken
	config.isSessionAuthenticated = options.isSessionAuthenticated
	config.onUnauthorized = options.onUnauthorized

	client.setConfig({
		baseUrl: import.meta.env.DEV
			? '/api'
			: import.meta.env.VITE_API_ENDPOINT,
		credentials: 'include',
		headers: import.meta.env.DEV
			? { 'ngrok-skip-browser-warning': 'true' }
			: {}
	})

	if (
		config.requestInterceptorId === undefined ||
		!client.interceptors.request.exists(config.requestInterceptorId)
	) {
		config.requestInterceptorId =
			client.interceptors.request.use(addCsrfToken)
	}

	if (
		config.responseInterceptorId === undefined ||
		!client.interceptors.response.exists(config.responseInterceptorId)
	) {
		config.responseInterceptorId =
			client.interceptors.response.use(handleUnauthorized)
	}
}

function getBrowserSdkConfig() {
	const sdkClient = client as ConfigurableClient
	sdkClient[BROWSER_SDK_CONFIG_KEY] ??= {}
	return sdkClient[BROWSER_SDK_CONFIG_KEY]
}

function readBrowserSdkConfig() {
	return (client as ConfigurableClient)[BROWSER_SDK_CONFIG_KEY]
}

function addCsrfToken(request: Request) {
	const config = readBrowserSdkConfig()
	const token = config?.queryClient
		? config.getCsrfToken?.(config.queryClient)
		: undefined
	if (!token) {
		return request
	}

	const headers = new Headers(request.headers)
	headers.set('X-CSRF-Token', token)
	return new Request(request, { headers })
}

let sessionRecovery: Promise<void> | undefined

async function handleUnauthorized(response: Response) {
	if (response.status !== 401) {
		return response
	}

	const config = readBrowserSdkConfig()
	const queryClient = config?.queryClient
	if (!(queryClient && config.isSessionAuthenticated?.(queryClient))) {
		return response
	}

	if (!isSessionDeathCode(await readUnauthorizedErrorCode(response))) {
		return response
	}

	sessionRecovery ??= recoverDeadSession(config, queryClient)
	await sessionRecovery
	return response
}

async function recoverDeadSession(
	config: BrowserSdkConfig,
	queryClient: QueryClient
) {
	try {
		queryClient.clear()
		await config.onUnauthorized?.()
	} finally {
		sessionRecovery = undefined
	}
}

async function readUnauthorizedErrorCode(response: Response) {
	try {
		const body: unknown = await response.clone().json()
		if (
			body &&
			typeof body === 'object' &&
			'code' in body &&
			typeof body.code === 'string'
		) {
			return body.code
		}
	} catch {
		return undefined
	}
	return undefined
}
